import type { ButtonRule } from "./activeMessages";
import type { AssistantSkill } from "./skills";
import type { TemplateDraft } from "@desfecha/contracts";
import { api, apiUrl } from "./client";
import type { Member, Team } from "./types";

const base = (slug: string) => `/v1/c/${slug}`;

export interface ChannelRow {
  id: string;
  kind: string;
  external_id: string;
  address: string | null;
  display_name: string | null;
  status: string;
  quality_rating: string | null;
  messaging_tier: string | null;
  mode: string;
  default_team_id: string | null;
  default_assistant_id?: string | null;
  default_flow_id?: string | null;
  account_id: string | null;
  account_external_id: string | null;
  account_name: string | null;
  created_at: string;
  name_status?: string | null;
  connection_method?: string | null;
  health?: ChannelHealth | null;
  health_checked_at?: string | null;
  has_pin?: boolean;
  /** When Meta's access for this number lapses (Embedded Signup tokens last 60 days, renewed automatically). */
  token_expires_at?: string | null;
  owner_business_id?: string | null;
  owner_business_name?: string | null;
  /** Coexistence: the one-time synchronization of the WhatsApp Business app's contacts and chats. */
  app_sync?: AppSync | null;
  /** A number brought by an import (decision 0024), waiting for its connection through Meta. */
  /** When the number was taken off DoneTalk on its card (absent when Meta turned it off). */
  disconnected_at?: string | null;
  imported_from?: {
    source: "copiloto";
    importId: string;
    numberId: number;
  } | null;
}
export interface AppSyncPart {
  status: "requested" | "running" | "done" | "declined" | "failed";
  error?: string;
  at: string;
  progress?: number;
  messages?: number;
  count?: number;
}
export interface AppSync {
  connectedAt: string;
  contacts?: AppSyncPart;
  history?: AppSyncPart;
}
/** Meta's answer to "can this number send?", up the chain number → account → business → app. */
export interface ChannelHealth {
  can_send_message: string;
  entities?: Array<{
    entity_type: string;
    id: string;
    can_send_message: string;
    errors?: Array<{
      error_code: number;
      error_description?: string;
      possible_solution?: string;
    }>;
    additional_info?: string[];
  }>;
}
export interface SignupConfigDto {
  available: boolean;
  appId: string | null;
  configId: string | null;
  graphVersion: string;
  coexistence: boolean;
  /** Who pays Meta: the customer's card, or a partner's credit line (Multi-Partner Solution). */
  billing: "customer" | "partner";
  solutionId: string | null;
}
/** Whether Meta opens access-code templates to a WhatsApp account (it does so over time). */
export interface AuthenticationAccess {
  allowed: boolean;
  reason: "not_verified" | "refused_by_meta" | null;
  verification: string | null;
  refusedAt: string | null;
  retryAt: string | null;
}

export interface TemplateRow {
  id: string;
  account_id: string;
  account_name?: string | null;
  external_id?: string | null;
  name: string;
  language: string;
  category: string;
  previous_category: string | null;
  status: string;
  quality: string | null;
  components: unknown[];
  rejection_reason?: string | null;
  synced_at: string | null;
  updated_at?: string;
  /** The draft to open in the editor; null when the template uses what the editor cannot write. */
  draft?: TemplateDraft | null;
  editable?: boolean;
  /** The saved sample of a media header, for the editor's preview. */
  sampleUrl?: string | null;
  /** What each quick-reply button does inside DoneTalk (decision 0036). */
  button_actions?: ButtonRule[];
}
export interface AiCallRow {
  id: string;
  created_at: string;
  surface: string;
  purpose: string | null;
  provider: string;
  model: string;
  key_source: "platform" | "customer";
  prompt_tokens: number;
  completion_tokens: number;
  cached_tokens: number;
  cost_usd: number;
  latency_ms: number | null;
  status: "success" | "error" | "blocked";
  error: string | null;
  conversation_id: string | null;
  member_name: string | null;
}
export interface BusinessHoursBody {
  name: string;
  timezone: string;
  weekly: Record<string, Array<{ from: string; to: string }>>;
  holidays?: string[];
}
export interface BusinessHoursRow extends BusinessHoursBody {
  id: string;
  /** Teams that answer on this schedule. */
  teams?: string[];
}
export interface AssistantFollowUp {
  when: import("./campaigns").FollowUpWhen;
  times: number;
  /** The days and hours it may follow up in; empty is any time. */
  window: Array<{ day: number; from: string; to: string }>;
}
export interface AssistantRow {
  id: string;
  name: string;
  description: string | null;
  /** Its own reply model, else the company's (decision 0037, D9). */
  reply_model?: import("./aiModels").ModelChoice | null;
  /** Follow-up outside campaigns (decision 0044, D6), on the assistant's own page. */
  follow_up?: AssistantFollowUp;
  status: "draft" | "active" | "archived";
  draft_version_id: string | null;
  active_version_id: string | null;
  active_number: number | null;
  draft_number: number | null;
  running: number;
  /** When the live version went live. */
  published_at?: string | null;
  /** The draft differs from what contacts get. */
  unpublished?: boolean;
  /** Of the version being edited (the draft, else the live one). */
  persona?: AssistantVersion["persona"] | null;
  knowledge_collection_ids?: string[] | null;
  handoff_team_name?: string | null;
  updated_at?: string;
}
export interface AssistantRun {
  id: string;
  conversation_id: string;
  status: "running" | "completed" | "handed_off" | "cancelled" | "failed";
  turns: number;
  handoff_reason: string | null;
  started_at: string;
  ended_at: string | null;
  contact_name: string | null;
}
export type AssistantTestAction =
  | { kind: "handoff"; reason: string; team: string | null }
  /** It opened one of its skills (decision 0035). */
  | { kind: "skill"; name: string }
  /** Mensagens ativas the contact asked for (decision 0036). */
  | {
      kind: "active_messages";
      action: "pause" | "stop" | "resume";
      until: string | null;
    }
  | { kind: "close"; outcome: string }
  | { kind: "set_field"; field: string; value: string }
  | { kind: "start_flow"; flow: string }
  | { kind: "end" }
  | { kind: "exit_keyword"; keyword: string; team: string | null }
  | { kind: "exit_field"; field: string; team: string | null }
  | { kind: "max_turns"; team: string | null }
  /** A scheduling tool that would have booked, moved or cancelled ("book:Avaliação:…"). */
  | { kind: "appointment"; detail: string }
  /** A company tool: what a reading returned, or what a change would have done. */
  | {
      kind: "tool";
      tool: string;
      mode:
        "read" | "preview" | "waiting" | "simulated" | "approval" | "missing";
      text: string | null;
    };
export interface AssistantTestResult {
  replies: string[];
  consulted: Array<{ title: string; collection: string | null }>;
  actions: AssistantTestAction[];
  ended: boolean;
  fields: Record<string, string>;
}
export interface AssistantVersion {
  id: string;
  number: number;
  persona: {
    name?: string;
    tone?: string;
    language?: string;
    temperature?: number;
  };
  instructions: string;
  knowledge_collection_ids: string[];
  tools: string[];
  exit_conditions: Array<
    | { when: "field_set"; field: string }
    | { when: "keyword"; keywords: string[] }
  >;
  content_filters: { blockedTerms?: string[]; fallbackText?: string };
  max_turns: number | null;
  handoff_team_id: string | null;
  scheduling?: Partial<AssistantScheduling> | null;
  /** The company's tools this version offers (decision 0033). */
  custom_tools?: AssistantCustomTool[] | null;
  skills?: AssistantSkill[] | null;
  knowledge_modes?: Record<string, KnowledgeMode> | null;
  published_at: string | null;
}
/** A company tool on an assistant, with its own "quando usar" when it has one. */
export interface AssistantCustomTool {
  toolId: string;
  description?: string | null;
}

/** How the assistant books (decision 0022): which types, how many times at once, how it offers. */
export interface AssistantScheduling {
  typeIds: string[];
  offer: number;
  handoffAfterRefusals: number;
  preferWithinDays: number | null;
  presentAs: "list" | "text";
  confirmBeforeBooking: boolean;
}
export interface AssistantDraft {
  persona: {
    name?: string;
    tone?: string;
    language?: string;
    temperature?: number;
  };
  instructions: string;
  knowledgeCollectionIds: string[];
  tools: string[];
  exitConditions: AssistantVersion["exit_conditions"];
  contentFilters: { blockedTerms: string[]; fallbackText?: string };
  maxTurns: number | null;
  handoffTeamId: string | null;
  scheduling: AssistantScheduling;
  customTools: AssistantCustomTool[];
  /** The skills it uses and how (decision 0035). */
  skills: AssistantSkill[];
  /** Per knowledge base: whole in the prompt, searched, or decided by size. */
  knowledgeModes: Record<string, KnowledgeMode>;
}
export type KnowledgeMode = "auto" | "whole" | "search";
export interface CollectionRow {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  documents: number;
  failed?: number;
  processing?: number;
  /** Source kinds of its contents: text, url, upload, faq. */
  sources?: string[];
  /** Assistants that read it, live or in a draft. */
  used_by?: string[];
}
export interface DocumentRow {
  id: string;
  title: string;
  source_kind: string;
  source_ref?: string | null;
  status: string;
  error: string | null;
  size_bytes: number | null;
  chunks: number;
  created_at: string;
  /** The original file is kept (uploads since October 2026). */
  has_file?: boolean;
  mime?: string | null;
}
/** One document as the assistant reads it. */
export interface DocumentDetail {
  id: string;
  collection_id: string;
  title: string;
  source_kind: string;
  source_ref: string | null;
  status: string;
  error: string | null;
  created_at: string;
  created_by_name: string | null;
  chunks: number;
  chars: number;
  file: { mime: string; size_bytes: number } | null;
}
export interface PolicyRow {
  id: string;
  kind: "lifecycle" | "gate";
  name: string;
  scope_kind: "customer" | "team" | "channel";
  scope_id: string | null;
  mode: string | null;
  priority: number;
  config: Record<string, unknown>;
  is_active: boolean;
}
export interface AiSettings {
  use_own_keys?: boolean;
  useOwnKeys?: boolean;
  monthly_budget_usd?: number | null;
  monthlyBudgetUsd?: number | null;
  budget_action?: string;
  budgetAction?: string;
  surface_overrides?: Record<string, { provider: string; model: string }>;
  surfaceOverrides?: Record<string, { provider: string; model: string }>;
}

export const adminApi = {
  channels: (slug: string) =>
    api<{ channels: ChannelRow[] }>(`${base(slug)}/channels`),
  /** Who answers a number and its team (also before an imported number is connected). */
  channelHandling: (
    slug: string,
    id: string,
    body: {
      first: "team" | "assistant" | "flow";
      assistantId: string | null;
      teamId: string | null;
    },
  ) =>
    api<{
      channel: {
        id: string;
        mode: string;
        default_assistant_id: string | null;
        default_team_id: string | null;
        default_flow_id: string | null;
      };
    }>(`${base(slug)}/channels/${id}/handling`, { method: "PUT", body }),
  /** Takes a number off DoneTalk; it stays at Meta and wherever else it is used. */
  disconnectChannel: (slug: string, id: string) =>
    api<{ channel: { id: string; status: string }; metaFailed: boolean }>(
      `${base(slug)}/channels/${id}/disconnect`,
      { method: "POST", body: {} },
    ),
  connectWhatsApp: (
    slug: string,
    body: {
      wabaId?: string;
      phoneNumberId?: string;
      accessToken?: string;
      code?: string;
      pin?: string;
      coexistence?: boolean;
      mode?: string;
      defaultTeamId?: string;
    },
  ) =>
    api<{
      channel: {
        id: string;
        address: string;
        displayName: string | null;
        nameStatus: string | null;
        health: ChannelHealth | null;
      };
      pin?: string;
    }>(`${base(slug)}/channels/whatsapp/connect`, { method: "POST", body }),
  signupConfig: (slug: string) =>
    api<SignupConfigDto>(`${base(slug)}/channels/whatsapp/signup-config`),
  checkHealth: (slug: string, channelId: string) =>
    api<{ health: ChannelHealth | null; checkedAt: string }>(
      `${base(slug)}/channels/${channelId}/health`,
      { method: "POST", body: {} },
    ),
  channelPin: (slug: string, channelId: string) =>
    api<{ pin: string }>(`${base(slug)}/channels/${channelId}/pin`),
  templates: (slug: string) =>
    api<{ templates: TemplateRow[] }>(`${base(slug)}/templates`),
  createTemplate: (slug: string, accountId: string, draft: TemplateDraft) =>
    api<{ template: TemplateRow }>(`${base(slug)}/templates`, {
      method: "POST",
      body: { accountId, draft },
    }),
  saveTemplate: (slug: string, id: string, draft: TemplateDraft) =>
    api<{ template: TemplateRow }>(`${base(slug)}/templates/${id}`, {
      method: "PUT",
      body: { draft },
    }),
  submitTemplate: (slug: string, id: string) =>
    api<{ template: TemplateRow }>(`${base(slug)}/templates/${id}/submit`, {
      method: "POST",
      body: {},
    }),
  deleteTemplate: (slug: string, id: string) =>
    api<{ ok: true }>(`${base(slug)}/templates/${id}`, { method: "DELETE" }),
  /** Whether the account may have access-code templates, and Meta's text for one. */
  templateAuthentication: (
    slug: string,
    accountId: string,
    opts: {
      language: string;
      securityRecommendation: boolean;
      expirationMinutes?: number | null;
    },
  ) =>
    api<{
      access: AuthenticationAccess;
      preview: {
        body: string;
        footer: string | null;
        button: string | null;
      } | null;
    }>(
      `${base(slug)}/templates/authentication?${new URLSearchParams({
        accountId,
        language: opts.language,
        securityRecommendation: String(opts.securityRecommendation),
        ...(opts.expirationMinutes
          ? { expirationMinutes: String(opts.expirationMinutes) }
          : {}),
      })}`,
    ),
  syncTemplates: (slug: string, accountId: string) =>
    api<{ synced: number; categoryChanges: number }>(
      `${base(slug)}/accounts/${accountId}/templates/sync`,
      { method: "POST", body: {} },
    ),
  members: (slug: string) =>
    api<{ members: Member[] }>(`${base(slug)}/members`),
  invite: (slug: string, body: { email: string; name: string; role: string }) =>
    api<{
      member: { id: string };
      inviteToken: string;
      expiresAt: string;
      /** The invitation also went by e-mail. */
      emailed?: boolean;
    }>(`${base(slug)}/members/invitations`, { method: "POST", body }),
  patchMember: (
    slug: string,
    id: string,
    body: { role?: string; status?: string },
  ) => api(`${base(slug)}/members/${id}`, { method: "PATCH", body }),
  presence: (
    slug: string,
    id: string,
    presence: string,
    pauseReason?: string,
  ) =>
    api(`${base(slug)}/members/${id}/presence`, {
      method: "PUT",
      body: { presence, pauseReason },
    }),
  teams: (slug: string) => api<{ teams: Team[] }>(`${base(slug)}/teams`),
  createTeam: (slug: string, body: Record<string, unknown>) =>
    api<{ team: { id: string } }>(`${base(slug)}/teams`, {
      method: "POST",
      body,
    }),
  patchTeam: (slug: string, id: string, body: Record<string, unknown>) =>
    api(`${base(slug)}/teams/${id}`, { method: "PATCH", body }),
  addTeamMember: (slug: string, teamId: string, memberId: string) =>
    api(`${base(slug)}/teams/${teamId}/members`, {
      method: "PUT",
      body: { memberId },
    }),
  removeTeamMember: (slug: string, teamId: string, memberId: string) =>
    api(`${base(slug)}/teams/${teamId}/members/${memberId}`, {
      method: "DELETE",
    }),
  businessHours: (slug: string) =>
    api<{ businessHours: BusinessHoursRow[] }>(`${base(slug)}/business-hours`),
  createBusinessHours: (slug: string, body: BusinessHoursBody) =>
    api(`${base(slug)}/business-hours`, { method: "POST", body }),
  saveBusinessHours: (slug: string, id: string, body: BusinessHoursBody) =>
    api(`${base(slug)}/business-hours/${id}`, { method: "PUT", body }),
  assistants: (slug: string) =>
    api<{
      assistants: AssistantRow[];
      tools: Array<{ name: string; description?: string }>;
    }>(`${base(slug)}/assistants`),
  assistant: (slug: string, id: string) =>
    api<{ assistant: AssistantRow; versions: AssistantVersion[] }>(
      `${base(slug)}/assistants/${id}`,
    ),
  createAssistant: (
    slug: string,
    body: { name: string; description?: string; draft?: AssistantDraft },
  ) =>
    api<{ assistant: { id: string } }>(`${base(slug)}/assistants`, {
      method: "POST",
      body,
    }),
  patchAssistant: (
    slug: string,
    id: string,
    body: {
      name?: string;
      description?: string;
      followUp?: AssistantFollowUp;
    },
  ) => api(`${base(slug)}/assistants/${id}`, { method: "PATCH", body }),
  saveAssistantDraft: (slug: string, id: string, draft: AssistantDraft) =>
    api(`${base(slug)}/assistants/${id}/draft`, { method: "PUT", body: draft }),
  restoreAssistantVersion: (slug: string, id: string, number: number) =>
    api<{ ok: true; activeVersionId: string; number: number }>(
      `${base(slug)}/assistants/${id}/versions/${number}/restore`,
      { method: "POST" },
    ),
  assistantRuns: (slug: string, id: string) =>
    api<{ runs: AssistantRun[] }>(`${base(slug)}/assistants/${id}/runs`),
  testAssistant: (
    slug: string,
    body: {
      draft: AssistantDraft;
      messages: Array<{ from: "contact" | "assistant"; text: string }>;
      fields: Record<string, string>;
      /** The test contact's phone, for the company tools that read. */
      phone?: string | null;
      /** The assistant on screen: the test answers with its own reply model. */
      assistantId?: string;
    },
  ) =>
    api<AssistantTestResult>(`${base(slug)}/assistants/test`, {
      method: "POST",
      body,
    }),
  publishAssistant: (slug: string, id: string) =>
    api<{ number: number }>(`${base(slug)}/assistants/${id}/publish`, {
      method: "POST",
      body: {},
    }),
  collections: (slug: string) =>
    api<{ collections: CollectionRow[] }>(
      `${base(slug)}/knowledge/collections`,
    ),
  createCollection: (
    slug: string,
    body: { name: string; description?: string },
  ) =>
    api<{ collection: { id: string } }>(`${base(slug)}/knowledge/collections`, {
      method: "POST",
      body,
    }),
  renameCollection: (slug: string, id: string, name: string) =>
    api(`${base(slug)}/knowledge/collections/${id}`, {
      method: "PATCH",
      body: { name },
    }),
  documents: (slug: string, collectionId: string) =>
    api<{ documents: DocumentRow[] }>(
      `${base(slug)}/knowledge/collections/${collectionId}/documents`,
    ),
  addDocument: (
    slug: string,
    collectionId: string,
    body: {
      title: string;
      text: string;
      sourceKind?: string;
      sourceRef?: string;
    },
  ) =>
    api<{ document: { id: string; chunks: number } }>(
      `${base(slug)}/knowledge/collections/${collectionId}/documents`,
      { method: "POST", body },
    ),
  deleteDocument: (slug: string, id: string) =>
    api(`${base(slug)}/knowledge/documents/${id}`, { method: "DELETE" }),
  document: (slug: string, id: string) =>
    api<{ document: DocumentDetail; text: string }>(
      `${base(slug)}/knowledge/documents/${id}`,
    ),
  /** Keeps the original of a document whose text was read in the browser. */
  keepDocumentFile: (slug: string, id: string, file: File) => {
    const form = new FormData();
    form.append("file", file, file.name);
    return api<{ file: { name: string; mime: string; size_bytes: number } }>(
      `${base(slug)}/knowledge/documents/${id}/file`,
      { method: "POST", body: form },
    );
  },
  /** The original, opened (a PDF in the browser) or downloaded. */
  documentFileUrl: (slug: string, id: string, download = false) =>
    apiUrl(
      `${base(slug)}/knowledge/documents/${id}/file`,
      download ? { download: 1 } : {},
    ),
  addSite: (
    slug: string,
    collectionId: string,
    body: { url: string; crawl: boolean },
  ) =>
    api<{
      documents: Array<{
        id: string;
        title: string;
        url: string;
        chunks: number;
      }>;
      skipped: Array<{ url: string; reason: string }>;
    }>(`${base(slug)}/knowledge/collections/${collectionId}/documents/url`, {
      method: "POST",
      body,
    }),
  searchKnowledge: (slug: string, query: string, collectionIds?: string[]) =>
    api<{ results: Array<{ title: string; content: string; score: number }> }>(
      `${base(slug)}/knowledge/search`,
      {
        method: "POST",
        body: { query, ...(collectionIds ? { collectionIds } : {}) },
      },
    ),
  policies: (slug: string) =>
    api<{ policies: PolicyRow[] }>(`${base(slug)}/policies`),
  createPolicy: (slug: string, body: Record<string, unknown>) =>
    api<{ policy: { id: string } }>(`${base(slug)}/policies`, {
      method: "POST",
      body,
    }),
  patchPolicy: (slug: string, id: string, body: Record<string, unknown>) =>
    api(`${base(slug)}/policies/${id}`, { method: "PATCH", body }),
  aiSettings: (slug: string) =>
    api<{ settings?: AiSettings } & AiSettings>(`${base(slug)}/ai/settings`),
  saveAiSettings: (slug: string, body: Record<string, unknown>) =>
    api(`${base(slug)}/ai/settings`, { method: "PUT", body }),
  aiKeys: (slug: string) =>
    api<{
      keys: Array<{
        id: string;
        provider_code?: string;
        providerCode?: string;
        label: string;
        is_active?: boolean;
        created_at?: string;
      }>;
    }>(`${base(slug)}/ai/keys`),
  addAiKey: (
    slug: string,
    body: {
      providerCode: string;
      label: string;
      apiKey: string;
      baseUrl?: string;
    },
  ) => api(`${base(slug)}/ai/keys`, { method: "POST", body }),
  deleteAiKey: (slug: string, id: string) =>
    api(`${base(slug)}/ai/keys/${id}`, { method: "DELETE" }),
  aiCalls: (
    slug: string,
    q: { month?: string; surface?: string; status?: string; before?: string },
  ) =>
    api<{ calls: AiCallRow[]; more: boolean; month: string }>(
      `${base(slug)}/ai/calls`,
      { query: { ...q, limit: 50 } },
    ),
  aiUsage: (slug: string, month?: string) =>
    api<Record<string, unknown>>(`${base(slug)}/ai/usage`, {
      query: { month },
    }),
};

export interface ApiKeyRow {
  id: string;
  name: string;
  prefix: string;
  environment: "live" | "test";
  /** Made before per-key scopes: acts as a full integration until scopes are chosen. */
  legacy: boolean;
  scopes: string[];
  allowedIps: string[];
  skipApproval: boolean;
  createdBy: string | null;
  createdByName: string | null;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
  createdAt?: string;
}
export interface ApiKeyInput {
  name: string;
  environment?: "live" | "test";
  scopes: string[];
  expiresAt?: string | null;
  allowedIps?: string[];
  skipApproval?: boolean;
}
export interface InboundHookRow {
  id: string;
  name: string;
  mapping: {
    phone?: string;
    name?: string;
    fields?: Record<string, string>;
    channelId?: string;
  };
  defaultTeamId: string | null;
  defaultFlowId: string | null;
  goal: string | null;
  isActive: boolean;
  createdAt: string;
}
export interface WebhookRow {
  id: string;
  url: string;
  events: string[];
  status: "active" | "paused" | "failing";
  failureCount: number;
  createdAt: string;
  updatedAt: string;
}
export interface DeliveryRow {
  id: string;
  eventId: string;
  eventType: string;
  status: string;
  attempt: number;
  responseCode: number | null;
  responseMs: number | null;
  nextAttemptAt: string | null;
  createdAt: string;
}

export const integrationsApi = {
  apiKeys: (slug: string) =>
    api<{ apiKeys: ApiKeyRow[] }>(`${base(slug)}/api-keys`),
  createApiKey: (slug: string, body: ApiKeyInput) =>
    api<{ apiKey: ApiKeyRow; key: string }>(`${base(slug)}/api-keys`, {
      method: "POST",
      body,
    }),
  updateApiKey: (slug: string, id: string, body: Partial<ApiKeyInput>) =>
    api<{ apiKey: ApiKeyRow }>(`${base(slug)}/api-keys/${id}`, {
      method: "PATCH",
      body,
    }),
  revokeApiKey: (slug: string, id: string) =>
    api(`${base(slug)}/api-keys/${id}`, { method: "DELETE" }),
  hooks: (slug: string) =>
    api<{ inboundHooks: InboundHookRow[] }>(`${base(slug)}/inbound-hooks`),
  createHook: (slug: string, body: Record<string, unknown>) =>
    api<{ inboundHook: InboundHookRow; token: string; path: string }>(
      `${base(slug)}/inbound-hooks`,
      { method: "POST", body },
    ),
  patchHook: (slug: string, id: string, body: Record<string, unknown>) =>
    api<{ ok: true; token?: string; path?: string }>(
      `${base(slug)}/inbound-hooks/${id}`,
      { method: "PATCH", body },
    ),
  webhooks: (slug: string) =>
    api<{ webhooks: WebhookRow[] }>(`${base(slug)}/webhooks`),
  createWebhook: (slug: string, body: { url: string; events: string[] }) =>
    api<{ webhook: WebhookRow; secret: string }>(`${base(slug)}/webhooks`, {
      method: "POST",
      body,
    }),
  patchWebhook: (
    slug: string,
    id: string,
    body: { url?: string; events?: string[]; status?: "active" | "paused" },
  ) => api(`${base(slug)}/webhooks/${id}`, { method: "PATCH", body }),
  deleteWebhook: (slug: string, id: string) =>
    api(`${base(slug)}/webhooks/${id}`, { method: "DELETE" }),
  testWebhook: (slug: string, id: string) =>
    api<{ deliveryId: string; eventId: string }>(
      `${base(slug)}/webhooks/${id}/test`,
      { method: "POST", body: {} },
    ),
  deliveries: (slug: string, id: string) =>
    api<{ deliveries: DeliveryRow[] }>(
      `${base(slug)}/webhooks/${id}/deliveries`,
    ),
};
